import React, { useState, useEffect, useCallback } from 'react';
import _ from '../../lodash-wrapper';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router-dom';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import MetaTags from '../shared/meta';
import LoadingBackdrop from '../shared/loadingBackdrop'
import Fab from '@material-ui/core/Fab';
import SearchIcon from '@material-ui/icons/Search';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';

import { GROUP_SEARCH_DEFAULT_PARAMS, SEARCHSCREEN_PAGE_SIZE } from '../../constants';
import { searchCourseGroup } from '../../actions/courseAction';
import {
    CourseCategory,
    CourseLanguage,
    CourseLevel,
    CourseMedium,
    CourseGroupOrdering,
} from '../../models/enums';
import CourseGroupSearchForm from './courseGroupSearchForm';
import CourseGroupSearchResultSection from './courseGroupSearchResultSection';
import { NotFoundError } from '../../models/error';
import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        paddingLeft: '0px',
        paddingRight: '0px',
    },
    mainContainer: {
        paddingLeft: '0px',
        paddingRight: '0px',
        background: '#FFFFFF',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch'
    },
    fab: {
        position: 'fixed',
        zIndex: 1000,
        top: '105px',
        bottom: 'auto',
        left: '20px',
        right: 'auto',
        [theme.breakpoints.up('sm')]: {
            top: '120px',
            bottom: 'auto',
            left: '30px',
            right: 'auto',
        },
        // horizontal offset, vertical offset, blur, spread
        boxShadow: '0 0 0 2px rgba(0,0,0,0.2)'
    }
}));

const convertURLParamsToState = params => {
    let state = {
        ...GROUP_SEARCH_DEFAULT_PARAMS
    };
    let page = _.parseInt(params.page);
    page = _.isInteger(page)?page:1;
    
    // Map simplified URL param 'discount_applied' to internal 'first_course_discount_applied'
    if (params.discount_applied !== undefined) {
        params.first_course_discount_applied = params.discount_applied;
    }
    
    // Parse ordering - default to LATEST
    let ordering = CourseGroupOrdering.LATEST;
    if (params.ordering) {
        const orderingKey = Object.keys(CourseGroupOrdering).find(
            key => CourseGroupOrdering[key].sortingId === params.ordering
        );
        if (orderingKey) {
            ordering = CourseGroupOrdering[orderingKey];
        }
    }
    
    _.keys(state).forEach(key => {
        let parsed = params[key], valid = false;
        switch(key) {
            case 'priceAmountMin':
            case 'priceAmountMax':
            case 'totalDurationMinuteMin':
            case 'totalDurationMinuteMax':
            case 'ratingAverageMin':
            case 'ratingAverageMax':
                parsed = _.parseInt(parsed);
                valid = _.isInteger(parsed);
                break;
            case 'medium':
                // Handle both single value and array
                if (Array.isArray(parsed)) {
                    valid = parsed.every(val => _.has(CourseMedium, val));
                } else if (parsed) {
                    valid = _.has(CourseMedium, parsed);
                }
                break;
            case 'language':
                // Handle both single value and array
                if (Array.isArray(parsed)) {
                    valid = parsed.every(val => _.has(CourseLanguage, val));
                } else if (parsed) {
                    valid = _.has(CourseLanguage, parsed);
                }
                break;
            case 'category':
                // queryString.parse with arrayFormat:'comma' handles this automatically
                if (Array.isArray(parsed)) {
                    valid = parsed.every(val => _.has(CourseCategory, val));
                } else if (parsed) {
                    valid = _.has(CourseCategory, parsed);
                }
                break;
            case 'courseLevel':
                // Handle both single value and array
                if (Array.isArray(parsed)) {
                    valid = parsed.every(val => _.has(CourseLevel, val));
                } else if (parsed) {
                    valid = _.has(CourseLevel, parsed);
                }
                break;
            case 'first_course_discount_applied':
                // Only apply filter if explicitly true, otherwise show all courses
                parsed = parsed === 'true' || parsed === true;
                valid = parsed === true;
                break;
            default:
                valid = _.has(GROUP_SEARCH_DEFAULT_PARAMS, key);
        }
        if(valid) {
            state[key] = parsed;
        }
    });
    return [state, page, ordering];
}

export const convertStateToURLQueryString = (state, page, ordering) => {
    // Use simplified 'discount_applied' in URL instead of 'first_course_discount_applied'
    const urlParams = {
        ...state,
        discount_applied: state.first_course_discount_applied || undefined,
        first_course_discount_applied: undefined, // Remove the long name from URL
        page: page === 1? undefined: page,
        ordering: ordering?.sortingId || undefined,
    };
    
    return queryString.stringify(urlParams, { skipNull: true, arrayFormat: 'comma' });
}

const executeSearch = async (
    dispatch,
    searchState,
    page,
    ordering,
    setResult,
    setError,
    setLoading
) => {
    setLoading(true);
    setError(null);
    let succeeded = true;
    try {
        // If RELEVANCE is selected but no query, fall back to LATEST
        const effectiveOrdering = (ordering === CourseGroupOrdering.RELEVANCE && !_.size(searchState.query))
            ? CourseGroupOrdering.LATEST
            : ordering;
        setResult(await dispatch(searchCourseGroup(searchState, page, effectiveOrdering, SEARCHSCREEN_PAGE_SIZE)));
    }
    catch (e) {
        succeeded = false;
        // This happens when page exceeds the max. no. of pages.
        // It is either due to user manually changing URL page params or a bug in navigation
        if(e instanceof NotFoundError) {
            e.message = '無效的篩選條件，正顯示所有課程';
        }
        setError(e);
    }
    setLoading(false);
    return succeeded;
}

const renderSearchForm = (
    classes,
    desktopVersion,
    searchFormInput,
    setSearchFormInput,
    onSubmitSearchForm,
    drawerOpen,
    setDrawerOpen,
) => {
    const formBody = (
        <CourseGroupSearchForm
            input={searchFormInput}
            setInput={setSearchFormInput}
            onSubmit={onSubmitSearchForm}
        />
    )
    if(desktopVersion)
        return formBody;
    return (
        <>
            <Fab
                className={classes.fab}
                color='primary'
                aria-label='search'
                onClick={() => setDrawerOpen(true)}
            >
                <SearchIcon />
            </Fab>
            <SwipeableDrawer anchor='left' open={drawerOpen} onOpen={() => {}} onClose={() => setDrawerOpen(false)}>
                {formBody}
            </SwipeableDrawer>
        </>
    );
}

export default function CourseGroupSearchScreen() {
    const theme = useTheme();
    const classes = useStyles(theme);
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const desktopVersion = useMediaQuery(theme.breakpoints.up('md'));

    const [loading, setLoading] = useState(true);
    const [initialStateFromURL, initialPageFromURL, initialOrderingFromURL] = convertURLParamsToState(queryString.parse(location.search, { arrayFormat: 'comma' }));
    const [initialState] = useState(initialStateFromURL);
    const [initialPage] = useState(initialPageFromURL);
    const [initialOrdering] = useState(initialOrderingFromURL);
    const [searchState, setSearchState] = useState(initialState);
    const [searchFormInput, setSearchFormInput] = useState(initialState);
    const [ordering, setOrdering] = useState(initialOrdering);
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [searchResult, setSearchResult] = useState(null);
    const [error, setError] = useState(null);
    const isSearchVariantPage = _.size(location.search) > 0;

    useEffect(() => {
        const executeInitialSearch = async () => {
            const succeeded = await executeSearch(
                dispatch,
                initialState,
                initialPage,
                initialOrdering,
                setSearchResult,
                setError,
                setLoading
            );
            // On error, reset search filter
            if(!succeeded) {
                setSearchState(GROUP_SEARCH_DEFAULT_PARAMS);
                setSearchFormInput(GROUP_SEARCH_DEFAULT_PARAMS);
                setOrdering(CourseGroupOrdering.LATEST);
                await executeSearch(
                    dispatch,
                    GROUP_SEARCH_DEFAULT_PARAMS,
                    1,
                    CourseGroupOrdering.LATEST,
                    setSearchResult,
                    () => {},
                    setLoading
                );
            }
        };
        executeInitialSearch();
        window.scrollTo(0, 0);
        
    }, [dispatch, initialState, initialPage, initialOrdering]);

    const onSubmitSearchForm = useCallback(async () => {
        setSearchState(searchFormInput);
        setCurrentPage(1);
        // Smart default: Use RELEVANCE for keyword search, LATEST otherwise
        // But respect user's manual sorting choice
        const hasKeywordQuery = _.size(searchFormInput.query) > 0;
        const newOrdering = hasKeywordQuery ? CourseGroupOrdering.RELEVANCE : CourseGroupOrdering.LATEST;
        setOrdering(newOrdering);
        const queryString = convertStateToURLQueryString(searchFormInput, 1, newOrdering);
        history.replace(`/search?${queryString}`);
        const succeeded = await executeSearch(
            dispatch,
            searchFormInput,
            1,
            newOrdering,
            setSearchResult,
            setError,
            setLoading,
        );
        if(succeeded) {
            setDrawerOpen(false);
        }
    }, [dispatch, history, searchFormInput, setSearchResult, setError, setLoading]);

    const onChangeOrdering = useCallback(async (newOrdering) => {
        setOrdering(newOrdering);
        setCurrentPage(1);
        const queryString = convertStateToURLQueryString(searchState, 1, newOrdering);
        history.replace(`/search?${queryString}`);
        await executeSearch(
            dispatch,
            searchState,
            1,
            newOrdering,
            setSearchResult,
            setError,
            setLoading
        );
    }, [dispatch, history, searchState, setSearchResult, setError, setLoading]);

    const requestPage = useCallback(async page => {
        setCurrentPage(page);
        const queryString = convertStateToURLQueryString(searchState, page, ordering);
        history.replace(`/search?${queryString}`);
        await executeSearch(
            dispatch,
            searchState,
            page,
            ordering,
            setSearchResult,
            setError,
            setLoading
        );
    }, [dispatch, history, searchState, ordering, setLoading, setSearchResult, setError]);

    return (
      <>
        <MetaTags
            id='course-group-search'
            titles={['搜尋學識']}
            description='搜尋學識 Hok6 的廣東話線上課程、講座與活動，按主題、語言、程度、價格等條件篩選合適內容。'
            url='/search'
            robots={isSearchVariantPage ? 'noindex,follow' : null}
            prerenderReady={searchResult !== null}
        />

        <main>
          <Card className={classes.root}>
            <LoadingBackdrop open={loading} />
            <Grid container className={classes.mainContainer}>
                {renderSearchForm(
                    classes,
                    desktopVersion,
                    searchFormInput,
                    setSearchFormInput,
                    onSubmitSearchForm,
                    drawerOpen,
                    setDrawerOpen
                )}
                <CourseGroupSearchResultSection
                    error={error}
                    searchResult={searchResult}
                    requestPage={requestPage}
                    currentPage={currentPage}
                    ordering={ordering}
                    onChangeOrdering={onChangeOrdering}
                    hasQuery={_.size(searchState.query) > 0}
                />
            </Grid>
          </Card>
        </main>
      </>
    );
}

CourseGroupSearchScreen.propTypes = {
}
